import React from 'react';

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import theme from 'core/lib/theme';

import { externalFormConfig } from '../externalFormConfig';

const { metrics } = externalFormConfig;

const { Title } = Typography;

type FinancialsRevenueSectionProps = {
  disabled: boolean;
};

const FinancialsRevenueSection = ({ disabled }: FinancialsRevenueSectionProps) => {
  const { t } = useTranslation(['dealbase', 'global']);

  return (
    <div>
      <Title className="my-2" level={5}>
        {t('dealbase:form.revenue.title')}
      </Title>
      <div className="flex lg:flex-row flex-col flex-auto gap-8">
        <div className="flex flex-col w-full lg:w-3/6">
          <Title style={{ color: theme.colors.Primary.rgba }} level={5}>
            {t('dealbase:form.metrics.headers.actuals')}
          </Title>
          <Form.List name="revenueYears">
            {(revenueYears, { add, remove }) => {
              return (
                <div className="flex flex-col">
                  <Form.Item
                    dependencies={[
                      ['revenueYears', metrics.revenue.year.dataIndex],
                      ['revenueYears', metrics.revenue.amount.dataIndex],
                    ]}
                  >
                    {({ getFieldValue }) => {
                      return revenueYears.map(({ key, name, ...restField }) => {
                        const disabled = getFieldValue('revenueYears')[name]?.lockedYear;
                        return (
                          <div key={key} className="flex flex-col">
                            <div className="flex items-center flex-row gap-x-4">
                              <div className="flex flex-auto sm:flex-row flex-col gap-x-4">
                                <Form.Item
                                  {...restField}
                                  className="md:w-3/6"
                                  label={metrics.revenue.year.label}
                                  rules={metrics.revenue.year.rules}
                                  name={[name, metrics.revenue.year.dataIndex]}
                                >
                                  <metrics.revenue.year.component disabled={disabled} />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  className="md:w-3/6"
                                  label={metrics.revenue.amount.label}
                                  rules={metrics.revenue.amount.rules}
                                  name={[name, metrics.revenue.amount.dataIndex]}
                                >
                                  <metrics.revenue.amount.component />
                                </Form.Item>
                              </div>
                              {!disabled && <MinusCircleOutlined onClick={() => remove(name)} />}
                            </div>
                            {revenueYears.length > 1 && <Divider />}
                          </div>
                        );
                      });
                    }}
                  </Form.Item>
                  <Form.Item>
                    <Button disabled={disabled} type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t('dealbase:actions.addYear')}
                    </Button>
                  </Form.Item>
                </div>
              );
            }}
          </Form.List>
        </div>
        <div className="flex flex-col w-full lg:w-3/6">
          <Title style={{ color: theme.colors.Primary.rgba }} level={5}>
            {t('dealbase:form.metrics.headers.forecast')}
          </Title>
          <Form.List name="revenueProjections">
            {(revenueProjections, { add, remove }) => {
              return (
                <div className="flex flex-col">
                  <Form.Item
                    dependencies={[
                      ['revenueProjections', metrics.revenueProjection.year.dataIndex],
                      ['revenueProjections', metrics.revenueProjection.amount.dataIndex],
                    ]}
                  >
                    {() =>
                      revenueProjections.map(({ key, name, ...restField }) => {
                        return (
                          <div key={key} className="flex flex-col">
                            <div className="flex items-center flex-row gap-x-4">
                              <div className="flex flex-auto sm:flex-row flex-col gap-x-4">
                                <Form.Item
                                  {...restField}
                                  className="md:w-3/6"
                                  label={metrics.revenueProjection.year.label}
                                  rules={metrics.revenueProjection.year.rules}
                                  name={[name, metrics.revenueProjection.year.dataIndex]}
                                >
                                  <metrics.revenueProjection.year.component disabled={disabled} />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  className="md:w-3/6"
                                  label={metrics.revenueProjection.amount.label}
                                  rules={metrics.revenueProjection.amount.rules}
                                  name={[name, metrics.revenueProjection.amount.dataIndex]}
                                >
                                  <metrics.revenueProjection.amount.component />
                                </Form.Item>
                              </div>
                              {!disabled && <MinusCircleOutlined onClick={() => remove(name)} />}
                            </div>
                            {revenueProjections.length > 1 && <Divider />}
                          </div>
                        );
                      })
                    }
                  </Form.Item>
                  <Form.Item>
                    <Button disabled={disabled} type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t('dealbase:actions.addYear')}
                    </Button>
                  </Form.Item>
                </div>
              );
            }}
          </Form.List>
        </div>
      </div>
    </div>
  );
};

export default FinancialsRevenueSection;
